import { ShareClassStats } from '../types/snapshot.types';
import { toNumber } from './to-number.util';

type ScenarioPoolShareClassRow = { change: number; id: string; name: string; value: number };

type ScenarioPoolShareClassSource = {
  defaultShareClass: ScenarioPoolShareClassRow;
  shareClasses: ScenarioPoolShareClassRow[];
};

export const mapCapTablePoolShareClassAmounts = (
  shareClasses: ShareClassStats[],
  poolShareClassId: string,
  amount: number,
): ShareClassStats[] =>
  shareClasses.map((shareClass) => ({
    ...shareClass,
    issued: shareClass.id === poolShareClassId ? amount : 0,
  }));

export const getScenarioPoolShareClassId = (
  pool: ScenarioPoolShareClassSource,
  defaultShareClassId: string,
): string => {
  if (toNumber(pool.defaultShareClass.value) !== 0) {
    return pool.defaultShareClass.id;
  }

  const otherShareClass = pool.shareClasses.find((shareClass) => toNumber(shareClass.value) !== 0);

  return otherShareClass?.id ?? defaultShareClassId;
};

export const mapScenarioPoolShareClassAmounts = ({
  pool,
  defaultShareClassId,
  amount,
  amountChange,
  poolShareClassId: poolShareClassIdOverride,
}: {
  amount: number;
  amountChange: number;
  defaultShareClassId: string;
  pool: { shareClassId?: string } & ScenarioPoolShareClassSource;
  poolShareClassId?: string;
}) => {
  const poolShareClassId =
    poolShareClassIdOverride ??
    pool.shareClassId ??
    getScenarioPoolShareClassId(pool, defaultShareClassId);

  return {
    defaultShareClass: {
      ...pool.defaultShareClass,
      value: poolShareClassId === defaultShareClassId ? amount : 0,
      change: poolShareClassId === defaultShareClassId ? amountChange : 0,
    },
    shareClasses: pool.shareClasses.map((shareClass) => ({
      ...shareClass,
      value: shareClass.id === poolShareClassId ? amount : 0,
      change: shareClass.id === poolShareClassId ? amountChange : 0,
    })),
  };
};

export const getScenarioFundraisingCommonStockTotal = ({
  stakeholderDefaultShareClassIssued,
  companyPools,
  scenarioPools,
  defaultShareClassId,
}: {
  companyPools: Array<
    { shareClassId?: string; unvested?: { value: number } } & ScenarioPoolShareClassSource
  >;
  defaultShareClassId: string;
  scenarioPools: Array<{ shareClassId?: string } & ScenarioPoolShareClassSource>;
  stakeholderDefaultShareClassIssued: number;
}): number =>
  toNumber(stakeholderDefaultShareClassIssued) +
  companyPools.reduce((sum, pool) => sum + toNumber(pool.defaultShareClass.value), 0) +
  getScenarioPoolUnvestedShareClassTotals(companyPools, defaultShareClassId, defaultShareClassId) +
  scenarioPools.reduce((sum, pool) => sum + toNumber(pool.defaultShareClass.value), 0);

export const getScenarioPoolUnvestedShareClassTotals = (
  pools: Array<
    { shareClassId?: string; unvested?: { value: number } } & ScenarioPoolShareClassSource
  >,
  defaultShareClassId: string,
  shareClassId: string,
): number =>
  pools.reduce((sum, pool) => {
    const poolShareClassId =
      pool.shareClassId ?? getScenarioPoolShareClassId(pool, defaultShareClassId);
    if (poolShareClassId !== shareClassId) {
      return sum;
    }

    return sum + toNumber(pool.unvested?.value);
  }, 0);
